import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { formatAccountRoleLabel } from "../data/accounts";
import {
  MESSAGE_POST_PAGE_LIMIT,
  canSendConsoleMessages,
  fetchMessagePosts,
  fetchMessageThread,
  markMessagePostDelivered,
  markMessagePostSeen,
  messageReceiptLine,
  sendMessagePost,
  type MessagePost,
  type MessageThreadDetail,
} from "../data/messages";

const RETENTION_COPY = "Kept with account records";

function messageClock(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
      timeZone,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
    }).format(date);
  }
}

function nextClientPostId(): string {
  return globalThis.crypto.randomUUID();
}

interface FailedSend {
  readonly body: string;
  readonly clientPostId: string;
  readonly reason: string;
}

export function MessageThread({
  accountId,
  accountRole,
  client,
  onThreadChanged,
  pollMs,
  threadId,
  timeZone,
}: {
  readonly accountId: string;
  readonly accountRole: string | null;
  readonly client: SupabaseClient;
  readonly onThreadChanged: () => void;
  readonly pollMs: number;
  readonly threadId: string;
  readonly timeZone: string;
}) {
  const [detail, setDetail] = useState<MessageThreadDetail | null>(null);
  const [posts, setPosts] = useState<readonly MessagePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [failedSend, setFailedSend] = useState<FailedSend | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const loadGenerationRef = useRef(0);
  const threadScopeRef = useRef(`${accountId}:${threadId}`);
  const receiptAttemptsRef = useRef<Set<string>>(new Set());
  const onThreadChangedRef = useRef(onThreadChanged);
  onThreadChangedRef.current = onThreadChanged;
  threadScopeRef.current = `${accountId}:${threadId}`;

  useEffect(() => {
    loadGenerationRef.current += 1;
    setDetail(null);
    setPosts([]);
    setFailure(null);
    setLoading(true);
    setLoadingOlder(false);
    setHasOlder(false);
    setDraft("");
    setSending(false);
    setFailedSend(null);
    receiptAttemptsRef.current.clear();
  }, [accountId, threadId]);

  useEffect(() => {
    const generation = ++loadGenerationRef.current;
    let active = true;
    setLoading(true);
    setFailure(null);

    async function load(): Promise<void> {
      const [detailLoad, postsLoad] = await Promise.all([
        fetchMessageThread(client, accountId, threadId),
        fetchMessagePosts(client, accountId, threadId),
      ]);
      if (!active || generation !== loadGenerationRef.current) return;
      if (detailLoad.status !== "ready") {
        setFailure(detailLoad.reason);
        setLoading(false);
        return;
      }
      setDetail(detailLoad.value);
      if (postsLoad.status !== "ready") {
        setFailure(postsLoad.reason);
        setLoading(false);
        return;
      }
      setPosts([...postsLoad.value].reverse());
      setHasOlder(postsLoad.value.length === MESSAGE_POST_PAGE_LIMIT);
      setLoading(false);
    }

    void load();
    const interval = pollMs > 0 ? setInterval(() => setReloadNonce((value) => value + 1), pollMs) : null;
    return () => {
      active = false;
      if (interval !== null) clearInterval(interval);
    };
  }, [accountId, client, pollMs, reloadNonce, threadId]);

  useEffect(() => {
    const incoming = posts.filter(
      (post) => !post.isOwnPost && !receiptAttemptsRef.current.has(post.postId),
    );
    if (incoming.length === 0) return;
    let active = true;
    for (const post of incoming) receiptAttemptsRef.current.add(post.postId);

    void Promise.all(
      incoming.map(async (post) => {
        const delivered = await markMessagePostDelivered(client, accountId, post.postId);
        if (!delivered.ok) return false;
        const seen = await markMessagePostSeen(client, accountId, post.postId);
        return seen.ok;
      }),
    ).then((outcomes) => {
      if (!active) return;
      incoming.forEach((post, index) => {
        if (!outcomes[index]) receiptAttemptsRef.current.delete(post.postId);
      });
      if (outcomes.some(Boolean)) onThreadChangedRef.current();
    });

    return () => {
      active = false;
    };
  }, [accountId, client, posts]);

  const loadOlder = useCallback(async () => {
    const oldest = posts[0];
    if (oldest === undefined || loadingOlder) return;
    const generation = loadGenerationRef.current;
    setLoadingOlder(true);
    const load = await fetchMessagePosts(client, accountId, threadId, oldest.postId);
    if (generation !== loadGenerationRef.current) {
      setLoadingOlder(false);
      return;
    }
    if (load.status !== "ready") {
      setFailure(load.reason);
      setLoadingOlder(false);
      return;
    }
    const known = new Set(posts.map((post) => post.postId));
    const older = [...load.value].reverse().filter((post) => !known.has(post.postId));
    setPosts([...older, ...posts]);
    setHasOlder(load.value.length === MESSAGE_POST_PAGE_LIMIT);
    setLoadingOlder(false);
  }, [accountId, client, loadingOlder, posts, threadId]);

  const roleAllowsSend = canSendConsoleMessages(accountRole);
  const canCompose = roleAllowsSend && detail?.thread.callerCanSend === true;
  const submit = useCallback(async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const body = draft.trim();
    if (!canCompose || sending || body === "") return;
    const clientPostId = failedSend?.body === body ? failedSend.clientPostId : nextClientPostId();
    const threadScope = threadScopeRef.current;
    setSending(true);
    setFailedSend(null);
    const outcome = await sendMessagePost(client, { accountId, threadId, body, clientPostId });
    if (threadScope !== threadScopeRef.current) return;
    setSending(false);
    if (!outcome.ok) {
      setFailedSend({ body, clientPostId, reason: outcome.reason });
      return;
    }
    setDraft("");
    setFailedSend(null);
    setReloadNonce((value) => value + 1);
    onThreadChangedRef.current();
  }, [accountId, canCompose, client, draft, failedSend, sending, threadId]);

  const memberLine = useMemo(() => {
    if (detail === null) return null;
    return detail.members
      .map((member) => `${member.displayName} · ${formatAccountRoleLabel(member.role)}`)
      .join(" · ");
  }, [detail]);

  if (loading && detail === null) {
    return <p className="messages-thread__state" role="status">Loading thread...</p>;
  }

  if (detail === null) {
    return (
      <div className="messages-thread__notice messages-thread__notice--error" role="alert">
        <p>{failure ?? "Could not load this thread."}</p>
        <button type="button" onClick={() => setReloadNonce((value) => value + 1)}>Try again</button>
      </div>
    );
  }

  return (
    <article className="messages-thread" aria-labelledby="messages-thread-title">
      <header className="messages-thread__head">
        <p className="messages-thread__eyebrow">
          Messages · {detail.thread.kind === "direct" ? "direct" : "crew thread"}
        </p>
        <h2 id="messages-thread-title">{detail.thread.name}</h2>
        <p className="messages-thread__members">
          {detail.thread.kind === "direct" ? "Direct" : `Crew · ${detail.thread.memberCount} members`}
          {memberLine === null ? "" : ` · ${memberLine}`}
        </p>
      </header>

      {failure !== null ? (
        <div className="messages-thread__notice messages-thread__notice--error" role="alert">
          <p>{failure}</p>
          <button type="button" onClick={() => setReloadNonce((value) => value + 1)}>Try again</button>
        </div>
      ) : null}

      {hasOlder ? (
        <button
          className="messages-thread__older"
          disabled={loadingOlder}
          type="button"
          onClick={() => void loadOlder()}
        >
          {loadingOlder ? "Loading..." : "Load older"}
        </button>
      ) : null}

      <ol className="messages-thread__posts" aria-label="Message history">
        {posts.length === 0 ? (
          <li className="messages-thread__empty">No messages in this thread yet.</li>
        ) : null}
        {posts.map((post) => (
          <li key={post.postId}>
            <article className={post.isOwnPost ? "messages-post messages-post--own" : "messages-post"}>
              <p className="messages-post__author">
                {post.authorDisplayName} · {formatAccountRoleLabel(post.authorRole)}
              </p>
              <p className="messages-post__body">{post.body}</p>
              <p className="messages-post__receipt">
                {post.isOwnPost
                  ? messageReceiptLine(post, (iso) => messageClock(iso, timeZone))
                  : messageClock(post.createdAt, timeZone)}
              </p>
            </article>
          </li>
        ))}
      </ol>

      {canCompose ? (
        <form className="messages-composer" aria-label={`Message ${detail.thread.name}`} onSubmit={(event) => void submit(event)}>
          <label className="messages-composer__field">
            <span className="messages-composer__label">Message</span>
            <textarea
              aria-label="Message"
              disabled={sending}
              maxLength={10000}
              placeholder={detail.thread.kind === "direct" ? `Message for ${detail.thread.name}` : "Message for this crew"}
              rows={3}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                if (failedSend !== null && event.target.value.trim() !== failedSend.body) setFailedSend(null);
              }}
            />
          </label>
          <button className="messages-composer__send" disabled={sending || draft.trim() === ""} type="submit">
            {sending ? "Sending..." : failedSend === null ? "Send" : "Retry"}
          </button>
        </form>
      ) : (
        <p className="messages-thread__readonly" role="status">
          Read only on Console. Supervisors and admins can send in this thread.
        </p>
      )}

      {failedSend !== null ? (
        <p className="messages-composer__failure" role="alert">Not sent · {failedSend.reason}</p>
      ) : null}
      <p className="messages-composer__retention">{RETENTION_COPY}</p>
    </article>
  );
}
